/**
 * The application shell.
 *
 * Its absence was the single largest gap between this codebase and the mockup: every screen was a
 * bare content column on an empty page, with no shield, no wordmark, no navigation and no identity.
 * The mockup's shell is not decoration — it is what makes the application feel like one artifact
 * rather than a set of forms, and it is the reverent surface the working panels sit inside
 * (10-design-system.md §Shell).
 *
 * ## Two modes, and why it is a preference rather than a breakpoint
 *
 * Top bar and left rail are both specified, **selectable per Account in interface preferences**. A
 * parish secretary on a wide monitor and a cleric on a laptop want different things, and the mockup
 * treats that as a stated choice rather than something guessed from viewport width. Until the
 * preferences screen lands the choice is held locally; the shape is already right for reading it
 * from the Account.
 *
 * ## Where the shell is deliberately absent
 *
 * The landing page, the auth screens and the **participant view** each carry their own chrome. The
 * participant one matters: somebody planning a funeral through a shared link is not browsing an
 * application, and giving them a navigation bar full of surfaces they cannot reach would be worse
 * than giving them nothing.
 */

import { useEffect, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../features/auth/useAuth';
import { fetchPreferences } from '../../features/preferences/api';
import { setLanguage } from '../../i18n';
import './app-shell.css';

/**
 * Routes that supply their own chrome. Matched as prefixes, longest first, so a nested route cannot
 * accidentally inherit the shell from a parent that does not want it.
 */
const UNSHELLED = [
  '/',
  '/login',
  '/register',
  '/password-reset',
  '/verify-email',
  '/access-unavailable',
];

function isUnshelled(pathname: string): boolean {
  if (pathname === '/') return true;
  // The participant view and the save-your-link screen are deliberately shell-free.
  if (/^\/liturgies\/[^/]+\/(participate|created)$/.test(pathname)) return true;
  return UNSHELLED.some((route) => route !== '/' && pathname.startsWith(route));
}

type Mode = 'bar' | 'rail';

const MODE_KEY = 'pius.nav-mode';

/**
 * Where somebody can actually go.
 *
 * ## Hiding here is usefulness, not security
 *
 * Worth being precise, because the two get conflated. Hiding a link protects **nothing** — the
 * server decides what every page returns, and one somebody may not see returns 404 there whether or
 * not this bar offered it (04-authorization.md). That rule says route-hiding is *insufficient*; it
 * does not say it is *wrong*. Offering a signed-out visitor a "Site" tab that will refuse them is
 * simply a bad interface.
 *
 * What is safe to gate on is the coarse, **server-stated** fact: is there an Account, and does
 * `/auth/me` report it as a Site Administrator. Neither is guessed here. Fine-grained per-resource
 * authority is still never guessed — that is what would produce navigation disagreeing with itself
 * between screens.
 *
 * `Templates` and `Organizations` stay visible to everyone deliberately: both are discoverable by
 * design, and somebody evaluating the product should see what an Official Wedding template contains
 * before registering.
 */
type Requires = 'anyone' | 'account' | 'site-admin';

interface NavItem {
  to: string;
  /** A key into the `common` namespace, never a literal — this bar is translated. */
  key: string;
  requires: Requires;
}

const PRIMARY: readonly NavItem[] = [
  // Creating a Liturgy needs no Account at all, so the dashboard is offered to anyone.
  { to: '/liturgies', key: 'nav.liturgies', requires: 'anyone' },
  { to: '/templates', key: 'nav.templates', requires: 'anyone' },
  { to: '/policies', key: 'nav.policies', requires: 'account' },
  { to: '/organizations', key: 'nav.organizations', requires: 'anyone' },
];

const SECONDARY: readonly NavItem[] = [
  // Both are Account-scoped surfaces: an inbox and a set of connected calendars belong to a person.
  { to: '/notifications', key: 'nav.notifications', requires: 'account' },
  { to: '/integrations', key: 'nav.integrations', requires: 'account' },
  { to: '/site', key: 'nav.site', requires: 'site-admin' },
];

/** The reader's coarse standing, as the server reports it. Never inferred from a route. */
interface Standing {
  hasAccount: boolean;
  isSiteAdministrator: boolean;
}

function visible(items: readonly NavItem[], standing: Standing): NavItem[] {
  return items.filter((item) => {
    if (item.requires === 'site-admin') return standing.isSiteAdministrator;
    if (item.requires === 'account') return standing.hasAccount;
    return true;
  });
}

export function AppShell({ children }: { children: React.ReactNode }): React.JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { t } = useTranslation('common');
  const { account } = useAuth();

  // Resolved once, from the same query every screen reads, so the bar and a page can never disagree
  // about who is asking.
  const standing: Standing = {
    hasAccount: account !== null,
    isSiteAdministrator: account?.is_site_administrator ?? false,
  };

  const [mode, setMode] = useState<Mode>(() =>
    (globalThis.localStorage?.getItem(MODE_KEY) as Mode | null) === 'rail' ? 'rail' : 'bar',
  );

  useEffect(() => {
    globalThis.localStorage?.setItem(MODE_KEY, mode);
  }, [mode]);

  // The Account's stored preferences, once there is an Account. Until then the browser's own
  // language and the locally-remembered navigation stand — a Participant has neither and needs
  // neither.
  const preferences = useQuery({
    queryKey: ['preferences'],
    queryFn: fetchPreferences,
    enabled: account !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    const stored = preferences.data;
    if (!stored) return;

    void setLanguage(stored.interface_language);
    // The stored vocabulary is the database's — `top_bar` / `left_rail`.
    setMode(stored.navigation === 'left_rail' ? 'rail' : 'bar');
  }, [preferences.data]);

  if (isUnshelled(pathname)) return <>{children}</>;

  return (
    <div className={`shell shell--${mode}`}>
      {/* The first thing a keyboard or screen-reader user meets. Without it, reaching the content
          means tabbing past the whole navigation on every single page. */}
      <a className="shell__skip" href="#content">
        {t('a11y.skip_to_content')}
      </a>

      {mode === 'bar' ? (
        <TopBar mode={mode} onMode={setMode} standing={standing} />
      ) : (
        <LeftRail mode={mode} onMode={setMode} standing={standing} />
      )}
      {/* `riseIn` on entry, keyed on the path so it plays per screen rather than once per session. */}
      <main className="shell__content" id="content" tabIndex={-1} key={pathname}>
        {children}
      </main>
    </div>
  );
}

// ── the mark ────────────────────────────────────────────────────────────────

/**
 * Shield plus typeset wordmark.
 *
 * `mix-blend-mode: multiply` against the warm surface, as the mockup renders it — the shield's white
 * ground disappears into the paper instead of sitting on it as a rectangle.
 *
 * The real `lockup-primary.png` is still missing from the repo (it exceeded the design tool's fetch
 * cap), so this is the shield with the wordmark set in Crimson Pro. Swap it when the file arrives.
 */
function Lockup({ stacked = false }: { stacked?: boolean }): React.JSX.Element {
  return (
    <Link
      className={stacked ? 'shell-lockup shell-lockup--stacked' : 'shell-lockup'}
      to="/liturgies"
    >
      <img className="shell-lockup__mark" src="/brand/shield.png" alt="" />
      <span className="shell-lockup__wordmark">
        PIUS <span className="shell-lockup__wordmark-second">SELECTIONS</span>
      </span>
    </Link>
  );
}

// ── top bar ─────────────────────────────────────────────────────────────────

function TopBar({
  mode,
  onMode,
  standing,
}: {
  mode: Mode;
  onMode: (m: Mode) => void;
  standing: Standing;
}): React.JSX.Element {
  const { t } = useTranslation('common');

  return (
    <header className="shell-bar">
      <Lockup />

      <nav className="shell-bar__nav" aria-label={t('a11y.navigation')}>
        {visible([...PRIMARY, ...SECONDARY], standing).map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="shell-bar__link"
            // A 2px cardinal-red underline marks the active item — the mockup's one use of red in
            // the chrome, and the reason no navigation item is ever a red button.
            activeProps={{ className: 'shell-bar__link shell-bar__link--on' }}
          >
            {t(item.key)}
          </Link>
        ))}
      </nav>

      {/* Offered to everyone, unlike the other primary action in this application: creating a
          celebration without an Account is a first-class path, not a trial of the real thing. */}
      <Link className="shell-bar__action" to="/liturgies/new">
        {t('nav.new_liturgy')}
      </Link>

      <Identity mode={mode} onMode={onMode} />
    </header>
  );
}

// ── left rail ───────────────────────────────────────────────────────────────

function LeftRail({
  mode,
  onMode,
  standing,
}: {
  mode: Mode;
  onMode: (m: Mode) => void;
  standing: Standing;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const secondary = visible(SECONDARY, standing);

  return (
    <nav className="shell-rail" aria-label={t('a11y.navigation')}>
      <Lockup stacked />

      <Link className="shell-rail__action" to="/liturgies/new">
        {t('nav.new', 'NEW')}
      </Link>

      <div className="shell-rail__group">
        {visible(PRIMARY, standing).map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="shell-rail__link"
            activeProps={{ className: 'shell-rail__link shell-rail__link--on' }}
          >
            {t(item.key)}
          </Link>
        ))}
      </div>

      {/* Grouped apart, as the mockup does: these are administrative surfaces, not planning ones.
          The whole group disappears when nothing in it applies, rather than leaving a bare rule. */}
      {secondary.length > 0 ? (
        <div className="shell-rail__group shell-rail__group--admin">
          {secondary.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="shell-rail__link"
              activeProps={{ className: 'shell-rail__link shell-rail__link--on' }}
            >
              {t(item.key)}
            </Link>
          ))}
        </div>
      ) : null}

      <div className="shell-rail__foot">
        <Identity mode={mode} onMode={onMode} stacked />
      </div>
    </nav>
  );
}

// ── identity ────────────────────────────────────────────────────────────────

/**
 * Who you are, and — when it is true — that you are here on a link rather than an account.
 *
 * The capability case is stated rather than left blank. Somebody administering a celebration through
 * a shared link has no name to show, and "signed out" would be a lie: they hold real authority. The
 * mockup's participant header says the same thing in its own words.
 */
function Identity({
  mode,
  onMode,
  stacked = false,
}: {
  mode: Mode;
  onMode: (m: Mode) => void;
  stacked?: boolean;
}): React.JSX.Element {
  // The same query the rest of the application reads, so the shell and a screen can never disagree
  // about who is asking.
  const { t } = useTranslation('common');
  const { account, capability } = useAuth();

  return (
    <div className={stacked ? 'shell-identity shell-identity--stacked' : 'shell-identity'}>
      <button
        type="button"
        className="shell-identity__mode"
        title={mode === 'bar' ? t('a11y.switch_to_rail') : t('a11y.switch_to_bar')}
        onClick={() => onMode(mode === 'bar' ? 'rail' : 'bar')}
        aria-label={mode === 'bar' ? t('a11y.switch_to_rail') : t('a11y.switch_to_bar')}
      >
        {mode === 'bar' ? '▤' : '▥'}
      </button>

      {account ? (
        <Link
          className="shell-identity__who"
          to="/preferences"
          title={`${account.display_name} (${account.email}) — Account & Preferences`}
        >
          {account.display_name}
        </Link>
      ) : capability ? (
        <span className="shell-identity__who shell-identity__who--capability">
          {capability.kind === 'administrator'
            ? t('identity.administrator_link')
            : t('identity.participant_link')}
          <span className="shell-identity__note">{t('identity.no_account_required')}</span>
        </span>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link className="shell-identity__who" to="/login">
            {t('nav.sign_in')}
          </Link>
          <span style={{ color: 'var(--muted)', fontSize: 11 }}>·</span>
          <Link className="shell-identity__who" to="/register">
            {t('nav.register', 'Register')}
          </Link>
        </div>
      )}
    </div>
  );
}
